import { TDropdownOption } from '@/shared/Inputs/DropdownInput/dropdown.types'
import {
  CmsResourceConfig,
  IFaqArticle,
  IFaqCategory,
  IGalleryCategory,
  IGalleryImage,
  IHeadliner,
  IMarketingBlockLineElement,
  IMarketingCard,
  IPartner,
  IScenePricingConfiguration,
  IExtraProduct,
  MarketingBlockLinePosition,
  ScenePricingConfigurationDay,
} from './cms.types'

const marketingLineOptions: TDropdownOption[] = [
  { value: 'FIRST_LINE', title: 'Первая линия' },
  { value: 'SECOND_LINE', title: 'Вторая линия' },
  { value: 'THIRD_LINE', title: 'Третья линия' },
]

const scenePricingDayOptions: TDropdownOption[] = [
  { value: 'FIRST', title: 'Первый день' },
  { value: 'SECOND', title: 'Второй день' },
]

export const marketingLinePositionLabels: Record<MarketingBlockLinePosition, string> = {
  FIRST_LINE: 'Первая линия',
  SECOND_LINE: 'Вторая линия',
  THIRD_LINE: 'Третья линия',
}

export const scenePricingDayLabels: Record<ScenePricingConfigurationDay, string> = {
  FIRST: 'Первый день',
  SECOND: 'Второй день',
}

const textColumn = <T extends Record<string, any>>(key: keyof T & string, header: string, width = '180px') => ({
  key,
  header,
  width,
  render: (item: T) => String(item[key] ?? ''),
  sortValue: (item: T) => String(item[key] ?? ''),
})

const numberColumn = <T extends Record<string, any>>(key: keyof T & string, header: string, width = '110px') => ({
  key,
  header,
  width,
  render: (item: T) => String(item[key] ?? 0),
  sortValue: (item: T) => Number(item[key] ?? 0),
})

const booleanColumn = <T extends Record<string, any>>(key: keyof T & string, header: string, width = '120px') => ({
  key,
  header,
  width,
  render: (item: T) => (item[key] ? 'Да' : 'Нет'),
  sortValue: (item: T) => Number(Boolean(item[key])),
})

const assetColumn = <T extends Record<string, any>>(key: keyof T & string, header: string, width = '220px') => ({
  key,
  header,
  width,
  render: (item: T) => String(item[key] ?? ''),
  sortValue: (item: T) => String(item[key] ?? ''),
})

export const cmsResources: CmsResourceConfig<any>[] = [
  {
    key: 'partners',
    title: 'Партнеры',
    singularTitle: 'партнера',
    endpoint: 'partners',
    idField: 'id',
    idType: 'number',
    group: 'content',
    searchFields: ['title', 'activeBackground', 'tooltipText'],
    fields: [
      { name: 'title', label: 'Название', kind: 'text', required: true },
      { name: 'logo', label: 'Логотип', kind: 'asset', required: true },
      { name: 'activeBackground', label: 'Активный фон', kind: 'text', nullable: true },
      { name: 'tooltipText', label: 'Подсказка', kind: 'textarea', nullable: true },
      { name: 'orderingIndex', label: 'Порядок', kind: 'number', required: true },
    ],
    columns: [
      numberColumn<IPartner>('id', 'ID', '72px'),
      textColumn<IPartner>('title', 'Название', '220px'),
      assetColumn<IPartner>('logo', 'Логотип'),
      textColumn<IPartner>('activeBackground', 'Активный фон', '220px'),
      textColumn<IPartner>('tooltipText', 'Подсказка', '260px'),
      numberColumn<IPartner>('orderingIndex', 'Порядок', '110px'),
    ],
    getSummary: (items) => `${items.length} записей`,
  },
  {
    key: 'faqCategories',
    title: 'FAQ категории',
    singularTitle: 'категорию FAQ',
    endpoint: 'faq/categories',
    idField: 'id',
    idType: 'number',
    group: 'faq',
    searchFields: ['title'],
    fields: [
      { name: 'title', label: 'Название', kind: 'text', required: true },
      { name: 'orderingIndex', label: 'Порядок', kind: 'number', required: true },
    ],
    columns: [
      numberColumn<IFaqCategory>('id', 'ID', '72px'),
      textColumn<IFaqCategory>('title', 'Название', '260px'),
      numberColumn<IFaqCategory>('orderingIndex', 'Порядок', '110px'),
      {
        key: 'articles',
        header: 'Статей',
        width: '110px',
        render: (item: IFaqCategory) => String(item.articles?.length || 0),
        sortValue: (item: IFaqCategory) => item.articles?.length || 0,
      },
    ],
    getSummary: (items) => `${items.length} категорий`,
  },
  {
    key: 'faqArticles',
    title: 'FAQ статьи',
    singularTitle: 'статью FAQ',
    endpoint: 'faq/articles',
    idField: 'id',
    idType: 'number',
    group: 'faq',
    searchFields: ['title', 'text'],
    fields: [
      {
        name: 'categoryId',
        label: 'Категория',
        kind: 'select',
        required: true,
        getOptions: (context) => context.faqCategories.map((category) => ({ value: category.id, title: category.title })),
      },
      { name: 'title', label: 'Заголовок', kind: 'text', required: true },
      { name: 'text', label: 'Текст', kind: 'markdown', required: true },
      { name: 'orderingIndex', label: 'Порядок', kind: 'number', required: true },
    ],
    columns: [
      numberColumn<IFaqArticle>('id', 'ID', '72px'),
      {
        key: 'categoryId',
        header: 'Категория',
        width: '220px',
        render: (item: IFaqArticle, context) =>
          context.faqCategories.find((category) => category.id === item.categoryId)?.title || `#${item.categoryId}`,
        sortValue: (item: IFaqArticle) => item.categoryId,
      },
      textColumn<IFaqArticle>('title', 'Заголовок', '260px'),
      textColumn<IFaqArticle>('text', 'Текст', '360px'),
      numberColumn<IFaqArticle>('orderingIndex', 'Порядок', '110px'),
    ],
    getSummary: (items) => `${items.length} статей`,
  },
  {
    key: 'marketingBlockLineElements',
    title: 'Маркетинговые линии',
    singularTitle: 'элемент линии',
    endpoint: 'marketing-block-line-elements',
    idField: 'id',
    idType: 'number',
    group: 'content',
    searchFields: ['title', 'position'],
    fields: [
      { name: 'position', label: 'Линия', kind: 'select', required: true, options: marketingLineOptions },
      { name: 'image', label: 'Изображение', kind: 'asset', required: true },
      { name: 'title', label: 'Заголовок', kind: 'text', required: true },
    ],
    columns: [
      numberColumn<IMarketingBlockLineElement>('id', 'ID', '72px'),
      {
        key: 'position',
        header: 'Линия',
        width: '180px',
        render: (item: IMarketingBlockLineElement) => marketingLinePositionLabels[item.position],
        sortValue: (item: IMarketingBlockLineElement) => item.position,
      },
      assetColumn<IMarketingBlockLineElement>('image', 'Изображение'),
      textColumn<IMarketingBlockLineElement>('title', 'Заголовок', '260px'),
    ],
    getSummary: (items) => `${items.length} элементов`,
  },
  {
    key: 'marketingCards',
    title: 'Маркетинговые карточки',
    singularTitle: 'карточку',
    endpoint: 'marketing-cards',
    idField: 'id',
    idType: 'number',
    group: 'content',
    searchFields: ['title', 'text'],
    fields: [
      { name: 'title', label: 'Заголовок', kind: 'text', required: true },
      { name: 'text', label: 'Текст', kind: 'textarea', required: true },
      { name: 'image', label: 'Изображение', kind: 'asset', required: true },
      { name: 'orderingIndex', label: 'Порядок', kind: 'number', required: true },
    ],
    columns: [
      numberColumn<IMarketingCard>('id', 'ID', '72px'),
      textColumn<IMarketingCard>('title', 'Заголовок', '240px'),
      textColumn<IMarketingCard>('text', 'Текст', '360px'),
      assetColumn<IMarketingCard>('image', 'Изображение'),
      numberColumn<IMarketingCard>('orderingIndex', 'Порядок', '110px'),
    ],
    getSummary: (items) => `${items.length} карточек`,
  },
  {
    key: 'galleryCategories',
    title: 'Галерея: категории',
    singularTitle: 'категорию галереи',
    endpoint: 'gallery/categories',
    idField: 'id',
    idType: 'number',
    group: 'gallery',
    searchFields: ['title'],
    fields: [{ name: 'title', label: 'Название', kind: 'text', required: true }],
    columns: [
      numberColumn<IGalleryCategory>('id', 'ID', '72px'),
      textColumn<IGalleryCategory>('title', 'Название', '260px'),
      {
        key: 'images',
        header: 'Изображений',
        width: '130px',
        render: (item: IGalleryCategory) => String(item.images?.length || 0),
        sortValue: (item: IGalleryCategory) => item.images?.length || 0,
      },
    ],
    getSummary: (items) => `${items.length} категорий`,
  },
  {
    key: 'galleryImages',
    title: 'Галерея: изображения',
    singularTitle: 'изображение',
    endpoint: 'gallery/images',
    idField: 'id',
    idType: 'number',
    group: 'gallery',
    searchFields: ['title', 'url'],
    fields: [
      {
        name: 'categoryId',
        label: 'Категория',
        kind: 'select',
        required: true,
        getOptions: (context) =>
          context.galleryCategories.map((category) => ({ value: category.id, title: category.title })),
      },
      { name: 'title', label: 'Alt текст', kind: 'text', required: true },
      { name: 'url', label: 'Изображение', kind: 'asset', required: true },
      { name: 'orderingIndex', label: 'Порядок', kind: 'number', required: true },
    ],
    columns: [
      numberColumn<IGalleryImage>('id', 'ID', '72px'),
      {
        key: 'categoryId',
        header: 'Категория',
        width: '220px',
        render: (item: IGalleryImage, context) =>
          context.galleryCategories.find((category) => category.id === item.categoryId)?.title || `#${item.categoryId}`,
        sortValue: (item: IGalleryImage) => item.categoryId,
      },
      textColumn<IGalleryImage>('title', 'Alt текст', '240px'),
      assetColumn<IGalleryImage>('url', 'Изображение'),
      numberColumn<IGalleryImage>('orderingIndex', 'Порядок', '110px'),
    ],
    getSummary: (items) => `${items.length} изображений`,
  },
  {
    key: 'scenePricingConfigurations',
    title: 'Цены сцен',
    singularTitle: 'цену сцены',
    endpoint: 'scene-pricing-configurations',
    idField: 'elementId',
    idType: 'string',
    getEntityId: (item: IScenePricingConfiguration) => `${encodeURIComponent(item.elementId)}/${item.day}`,
    group: 'commerce',
    searchFields: ['elementId', 'day', 'title', 'description', 'paymentLink', 'isSoldOut'],
    fields: [
      { name: 'elementId', label: 'Element ID', kind: 'text', required: true, readonlyOnEdit: true },
      { name: 'day', label: 'День', kind: 'select', required: true, options: scenePricingDayOptions, defaultValue: 'FIRST' },
      { name: 'title', label: 'Название', kind: 'text', nullable: true },
      { name: 'description', label: 'Описание', kind: 'textarea', nullable: true },
      { name: 'price', label: 'Цена', kind: 'number', required: true },
      { name: 'paymentLink', label: 'Ссылка на оплату', kind: 'text', required: true },
      { name: 'isSoldOut', label: 'Распродано', kind: 'boolean', defaultValue: false },
    ],
    columns: [
      textColumn<IScenePricingConfiguration>('elementId', 'Element ID', '220px'),
      {
        key: 'day',
        header: 'День',
        width: '150px',
        render: (item: IScenePricingConfiguration) => scenePricingDayLabels[item.day],
        sortValue: (item: IScenePricingConfiguration) => item.day,
      },
      textColumn<IScenePricingConfiguration>('title', 'Название', '220px'),
      textColumn<IScenePricingConfiguration>('description', 'Описание', '320px'),
      numberColumn<IScenePricingConfiguration>('price', 'Цена', '120px'),
      textColumn<IScenePricingConfiguration>('paymentLink', 'Ссылка на оплату', '360px'),
      booleanColumn<IScenePricingConfiguration>('isSoldOut', 'Распродано'),
    ],
    getSummary: (items) => `${items.length} цен`,
  },
  {
    key: 'extraProducts',
    title: 'Дополнительные товары',
    singularTitle: 'товар',
    endpoint: 'extra-products',
    idField: 'id',
    idType: 'number',
    group: 'commerce',
    searchFields: ['title', 'description', 'paymentLink', 'isForSale', 'isSoldOut'],
    fields: [
      { name: 'title', label: 'Название', kind: 'text', required: true },
      { name: 'description', label: 'Описание', kind: 'textarea', required: true },
      { name: 'price', label: 'Цена', kind: 'number', required: true },
      { name: 'image', label: 'Изображение', kind: 'asset', required: true },
      { name: 'paymentLink', label: 'Ссылка на оплату', kind: 'text', required: true },
      { name: 'isForSale', label: 'В продаже', kind: 'boolean', defaultValue: true },
      { name: 'isSoldOut', label: 'Распродан', kind: 'boolean', defaultValue: false },
      { name: 'orderingIndex', label: 'Порядок', kind: 'number', required: true },
    ],
    columns: [
      numberColumn<IExtraProduct>('id', 'ID', '72px'),
      textColumn<IExtraProduct>('title', 'Название', '220px'),
      textColumn<IExtraProduct>('description', 'Описание', '320px'),
      numberColumn<IExtraProduct>('price', 'Цена', '120px'),
      assetColumn<IExtraProduct>('image', 'Изображение'),
      booleanColumn<IExtraProduct>('isForSale', 'В продаже'),
      booleanColumn<IExtraProduct>('isSoldOut', 'Распродан'),
      numberColumn<IExtraProduct>('orderingIndex', 'Порядок', '110px'),
    ],
    getSummary: (items) => `${items.length} товаров`,
  },
  {
    key: 'headliners',
    title: 'Хедлайнеры',
    singularTitle: 'хедлайнера',
    endpoint: 'headliners',
    idField: 'id',
    idType: 'number',
    group: 'content',
    searchFields: ['title', 'image'],
    fields: [
      { name: 'title', label: 'Название', kind: 'text', required: true },
      { name: 'image', label: 'Изображение', kind: 'asset', required: true },
      { name: 'orderingIndex', label: 'Порядок', kind: 'number', required: true },
    ],
    columns: [
      numberColumn<IHeadliner>('id', 'ID', '72px'),
      textColumn<IHeadliner>('title', 'Название', '240px'),
      assetColumn<IHeadliner>('image', 'Изображение'),
      numberColumn<IHeadliner>('orderingIndex', 'Порядок', '110px'),
    ],
    getSummary: (items) => `${items.length} записей`,
  },
]

export const cmsResourceMap = cmsResources.reduce<Record<string, CmsResourceConfig<any>>>((map, resource) => {
  map[resource.key] = resource
  return map
}, {})

export const cmsNavigationGroups = [
  {
    title: 'Контент',
    items: cmsResources.filter((resource) => resource.group === 'content'),
  },
  {
    title: 'FAQ',
    items: cmsResources.filter((resource) => resource.group === 'faq'),
  },
  {
    title: 'Галерея',
    items: cmsResources.filter((resource) => resource.group === 'gallery'),
  },
  {
    title: 'Продажи',
    items: cmsResources.filter((resource) => resource.group === 'commerce'),
  },
]
